import { assertNever } from "app/common/assert";
import { readCookie, writeCookie } from "app/common/cookies";
import { PlayerColor } from "app/game/colors";
import { ITerritory } from "app/game/interfaces";
import { makeElementDraggable } from "app/ui/draggable";
import * as $ from "jquery";

export interface ITerritoryInfo {
    territory: ITerritory;
    playerColor: PlayerColor;
    numTroops: number;
}

export interface IReinforcementsInfo {
    numTroops: number;
}

type Pinned = "pinned";
type Floating = "floating";
type PinSetting = Pinned | Floating;

const containerId = "map";
export const troopsMoveUIDomId = "troops-move-ui";
const pinButtonId = `${troopsMoveUIDomId}-pin`;
const cancelButtonId = `${troopsMoveUIDomId}-cancel`;
const fromTroopsId = `${troopsMoveUIDomId}-from-troops`;
const toTroopsId = `${troopsMoveUIDomId}-to-troops`;

const pinnedSettingCookie = "troops-move-ui-pinned";

export interface IInGameActionUIHandler {
    buildControlTemplate(): string;
    buildTopActionsTemplate(): string;
    buildBottomActionsTemplate(): string;

    onShow(updater: IInGameActionUIUpdater): void;
    onCancel(): void;

    onFromTroopsClicked(): void;
    onToTroopsClicked(): void;
}

export interface IInGameActionUIUpdater {
    element: JQuery<HTMLElement>;
    isDisposed(): boolean;

    setFromTroops(numTroops: number): void;
    setToTroops(numTroops: number): void;

    close(): void;
}

export class InGameActionUI {

    private element: JQuery<HTMLElement>;

    public constructor(
        private readonly child: IInGameActionUIHandler,
        private readonly from: ITerritoryInfo | IReinforcementsInfo,
        private readonly to: ITerritoryInfo,
        private readonly canClose: boolean = true,
    ) {}

    public show(): void {
        if ($(`#${troopsMoveUIDomId}`).length) {
            throw new Error("Only one troops move UI can be active at the same time.");
        }

        this.element = $(this.buildElement());
        $(`#${containerId}`).append(this.element);

        const pinned = this.getPinSetting();

        switch (pinned) {
            case "floating":
                makeElementDraggable(this.element);
                this.positionElement();
                break;
            case "pinned":
                this.element.addClass("pinned");
                break;
            default: assertNever(pinned);
        }

        this.initializeEvents();

        this.child.onShow(
            {
                close: () => this.close(),
                element: this.element,
                isDisposed: () => this.element == null,
                setFromTroops: (numTroops: number) => this.setFromTroops(numTroops),
                setToTroops: (numTroops: number) => this.setToTroops(numTroops),
            });
    }

    public close(): void {
        this.element.remove();
        this.element = null;
    }

    public togglePin(): void {
        const setting = this.getPinSetting();
        let newSetting: PinSetting;

        switch (setting) {
            case "floating":
                newSetting = "pinned";
                break;
            case "pinned":
                newSetting = "floating";
                break;
            default: assertNever(setting);
        }

        writeCookie(pinnedSettingCookie, newSetting);

        this.close();
        this.show();
    }

    private getPinSetting(): PinSetting {
        return (readCookie(pinnedSettingCookie) ?? "floating") as PinSetting;
    }

    private positionElement(): void {
        const territoryCircleSize = 36;

        // Vertical positioning:
        // - Default the element to above the target territory
        // - If the source territory is above the target territory, switch to below
        const yMargin = 8;

        let above = true;
        if ("territory" in this.from && this.from.territory.y < this.to.territory.y) {
            above = false;
        }

        if (above) {
            this.element.css({ top: this.to.territory.y - yMargin - this.element.outerHeight() });
        } else {
            this.element.css({ top: this.to.territory.y + yMargin + territoryCircleSize });
        }

        // Horizontal positioning:
        // - Default the element to centred with the target territory
        // - Push the element inward from the two sides if it is too close to the edges
        let xPos = this.to.territory.x - .5 * this.element.outerWidth() + .5 * territoryCircleSize;
        const sidePadding = 16;
        const containterWidth = $(`#${containerId}`).width();

        if (xPos <= sidePadding) {
            xPos = sidePadding;
        }
        if (xPos >= containterWidth - sidePadding - this.element.outerWidth()) {
            xPos = containterWidth - sidePadding - this.element.outerWidth();
        }

        this.element.css({ left: xPos });
    }

    private initializeEvents(): void {
        this.element.find(`#${pinButtonId}`).on("click", () => this.togglePin());
        this.element.find(`#${cancelButtonId}`).on("click", () => this.onCancelButtonClicked());

        this.element.find(`#${fromTroopsId}`).on("click", () => this.child.onFromTroopsClicked());
        this.element.find(`#${toTroopsId}`).on("click", () => this.child.onToTroopsClicked());
    }

    private onCancelButtonClicked(): boolean {
        this.child.onCancel();
        this.close();
        return false; // suppress default event
    }

    private setFromTroops(numTroops: number): void {
        this.element.find(`#${fromTroopsId}`).html(`${numTroops}`);
    }

    private setToTroops(numTroops: number): void {
        this.element.find(`#${toTroopsId}`).html(`${numTroops}`);
    }

    private buildElement(): string {
        return `
            <div id="${troopsMoveUIDomId}" class="troops-modal">
                ${this.buildTemplate()}
            </div>
        `;
    }

    private buildTemplate(): string {
        const fromColorClass = ("playerColor" in this.from)
            ? colorClass(this.from.playerColor)
            : "";
        const fromString = ("territory" in this.from)
            ? fromToString("From", this.from.territory.name)
            : "";
        const toColorClass = colorClass(this.to.playerColor);
        const toString = fromToString("To", this.to.territory.name);

        const pinButton = `
            <a class="icon-button" id="${pinButtonId}" href="#">
                <i class="fa fa-thumb-tack"></i>
            </a>`;
        const closeButtonMaybe = this.canClose
            ? `
                <a class="icon-button" id="${cancelButtonId}" href="#">
                    <i class="fa fa-times"></i>
                </a>`
            : "";

        return `
            <div class="top">
                <div class="source left">
                    ${fromString}
                </div>
                <div class="actions">
                    ${this.child.buildTopActionsTemplate()}
                </div>
                <div class="right">
                    ${pinButton}
                    ${closeButtonMaybe}
                </div>
            </div>
            <div class="body">
                <div class="from-troops territory-large ${fromColorClass} selected">
                    <a href="#" id="${fromTroopsId}">#</a>
                </div>
                <div class="control">
                    ${this.child.buildControlTemplate()}
                </div>
                <div class="to-troops territory-large ${toColorClass} target">
                    <a href="#" id="${toTroopsId}">#</a>
                </div>
            </div>
            <div class="bottom">
                <div class="left"></div>
                <div class="actions">
                    ${this.child.buildBottomActionsTemplate()}
                </div>
                <div class="target right">
                    ${toString}
                </div>
            </div>
        `;
    }
}

function fromToString(labelText: string, territoryName: string) {
    return `<span class="from-to">${labelText}:</span>&nbsp;` +
        `<span class="terr-name" title="${territoryName}">${territoryName}</span>`;
}

function colorClass(color: PlayerColor) {
    return `tc-${color}`;
}
